<template>
    <CollapsibleRoot v-model:open="open" as-child>
        <nav
            class="w-full xl:hidden"
            tabindex="0"
            :class="{ 'relative flex-col': open }"
            @keydown.esc="open = false"
        >
            <div class="container flex justify-between">
                <div class="flex items-center">
                    <LogoLink class="xl:hidden" />
                </div>
                <div class="-mr-4 flex w-full flex-1 justify-end">
                    <CollapsibleTrigger
                        class="flex h-16 w-12 items-center justify-center text-gray-800 hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                        aria-controls="mobile-menu-panel"
                    >
                        <span class="sr-only">Menu</span>
                        <Icon
                            :name="!open ? 'bars-solid' : 'close'"
                            :class="[!open ? 'text-[1.8rem]' : 'text-[1.4rem]']"
                        />
                    </CollapsibleTrigger>
                </div>
            </div>
            <FadeTransition>
                <div
                    v-show="open"
                    id="mobile-menu-panel"
                    class="absolute left-0 right-0 top-full z-[999999] min-h-screen overflow-hidden bg-white focus:outline-none"
                >
                    <!-- Main Menu -->
                    <Transition
                        enter-active-class="transition-transform duration-300"
                        leave-active-class="transition-transform duration-300"
                        enter-from-class="-translate-x-full"
                        leave-to-class="-translate-x-full"
                    >
                        <div
                            v-show="!isSubmenuVisible"
                            class="absolute inset-0 h-full overflow-y-auto bg-white px-4"
                            :style="'padding-bottom: ' + paddingBottom + 'px'"
                        >
                            <!-- Top Level Menu Items -->
                            <div class="flex flex-col">
                                <button
                                    v-for="(
                                        menuDropdown, menuIndex
                                    ) in data?.menu_dropdowns"
                                    :key="menuIndex"
                                    class="flex items-center justify-between border-b border-gray-200 py-4 text-left text-xl font-semibold last:border-b-0"
                                    @click="openSubmenu(menuIndex)"
                                >
                                    <span>{{ menuDropdown.title }}</span>
                                    <Icon
                                        name="chevron-right"
                                        class="text-base"
                                    />
                                </button>
                            </div>

                            <!-- Action Buttons -->
                            <div
                                v-if="
                                    data?.action_buttons &&
                                    data.action_buttons.length > 0
                                "
                                class="pt-2"
                            >
                                <div
                                    v-for="(
                                        actionButton, actionIndex
                                    ) in data.action_buttons"
                                    :key="actionIndex"
                                    class="flex flex-col gap-2"
                                >
                                    <NuxtLink
                                        v-for="(
                                            btn, btnIndex
                                        ) in actionButton.button"
                                        :key="btnIndex"
                                        :to="
                                            isActive
                                                ? urlFromStoryblokLinkField(
                                                      btn.link,
                                                  )
                                                : undefined
                                        "
                                        :target="btn.link?.target"
                                        :class="'btn-' + btn.style"
                                        class="!inline-flex items-center justify-center gap-2"
                                        @click="open = false"
                                    >
                                        <Icon
                                            v-if="actionButton.icon"
                                            :name="actionButton.icon"
                                            class="text-[20px]"
                                        />
                                        {{ btn.label }}
                                    </NuxtLink>
                                </div>
                            </div>

                            <!-- Featured Link -->
                            <div v-if="data?.featured_link">
                                <NuxtLink
                                    :to="
                                        isActive
                                            ? data.featured_link.url
                                            : undefined
                                    "
                                    class="group flex items-center justify-center whitespace-nowrap py-3 pt-4 text-vibe transition-all hover:text-brew"
                                    @click="open = false"
                                >
                                    <Icon
                                        name="left-accent-icon"
                                        class="text-lg"
                                    />
                                    <span class="font-accent text-xl">{{
                                        data.featured_link_text ||
                                        data.featured_link.label
                                    }}</span>
                                    <Icon
                                        name="right-accent-icon"
                                        class="text-lg"
                                    />
                                </NuxtLink>
                            </div>
                        </div>
                    </Transition>

                    <!-- Submenu -->
                    <Transition
                        enter-active-class="transition-transform duration-300"
                        leave-active-class="transition-transform duration-300"
                        enter-from-class="translate-x-full"
                        leave-to-class="translate-x-full"
                        @after-leave="onSubmenuTransitionEnd"
                    >
                        <div
                            v-show="isSubmenuVisible"
                            class="absolute inset-0 z-10 h-full overflow-y-auto bg-white px-4"
                            :style="'padding-bottom: ' + paddingBottom + 'px'"
                        >
                            <!-- Submenu Content -->
                            <div v-show="activeSubmenu" class="pb-28">
                                <div
                                    class="flex items-center gap-2 border-b border-gray-200"
                                >
                                    <button
                                        class="flex grow items-center gap-4 py-5 transition-colors"
                                        aria-label="Back to main menu"
                                        @click="closeSubmenu"
                                    >
                                        <Icon
                                            name="chevron-left"
                                            class="text-lg"
                                        />
                                        <div
                                            class="text-xl font-semibold text-grind"
                                        >
                                            {{ activeSubmenu?.title }}
                                        </div>
                                    </button>
                                </div>

                                <div
                                    v-for="(
                                        column, columnIndex
                                    ) in activeSubmenu?.columns"
                                    :key="columnIndex"
                                    :class="{
                                        'border-b border-gray-200 last:border-b-0':
                                            activeSubmenu?.columns_separator,
                                    }"
                                >
                                    <!-- Thumbnail Column -->
                                    <div v-if="'thumbnails' in column">
                                        <button
                                            v-if="column.title"
                                            class="flex w-full items-center justify-between py-4 text-left text-lg font-semibold text-grind transition-colors"
                                            @click="
                                                toggleColumn(
                                                    activeMenuIndex!,
                                                    columnIndex,
                                                )
                                            "
                                        >
                                            <span>{{ column.title }}</span>
                                            <Icon
                                                :name="
                                                    isColumnExpanded(
                                                        activeMenuIndex!,
                                                        columnIndex,
                                                    )
                                                        ? 'chevron-up'
                                                        : 'chevron-down'
                                                "
                                                class="text-base"
                                            />
                                        </button>
                                        <Transition
                                            enter-active-class="transition-all duration-300 ease-in-out"
                                            leave-active-class="transition-all duration-300 ease-in-out"
                                            enter-from-class="max-h-0 opacity-0"
                                            enter-to-class="max-h-screen opacity-100"
                                            leave-from-class="max-h-screen opacity-100"
                                            leave-to-class="max-h-0 opacity-0"
                                        >
                                            <div
                                                v-show="
                                                    !column.title ||
                                                    isColumnExpanded(
                                                        activeMenuIndex!,
                                                        columnIndex,
                                                    )
                                                "
                                            >
                                                <p
                                                    v-if="column.description"
                                                    class="mb-3 text-sm text-grind-800"
                                                >
                                                    {{ column.description }}
                                                </p>
                                                <div
                                                    class="mb-8 grid grid-cols-3 gap-4 md:grid-cols-4 lg:grid-cols-6"
                                                >
                                                    <NuxtLink
                                                        v-for="(
                                                            thumb, thumbIndex
                                                        ) in column.thumbnails"
                                                        :key="thumbIndex"
                                                        :to="
                                                            isActive
                                                                ? urlFromStoryblokLinkField(
                                                                      thumb.link,
                                                                  )
                                                                : undefined
                                                        "
                                                        class="group relative flex flex-col items-center transition-all"
                                                        @click="open = false"
                                                    >
                                                        <div
                                                            class="mb-1 rounded-2xl bg-gray-100 p-1 group-hover:bg-gray-300"
                                                        >
                                                            <StoryblokImage
                                                                :src="
                                                                    thumb.image
                                                                        .src
                                                                "
                                                                :alt="
                                                                    thumb.image
                                                                        .alt ||
                                                                    ''
                                                                "
                                                                class="flex aspect-square items-center justify-center overflow-hidden rounded-xl object-contain"
                                                                sizes="160px"
                                                                :image-width="
                                                                    160
                                                                "
                                                                :image-height="
                                                                    160
                                                                "
                                                            />
                                                        </div>
                                                        <div
                                                            class="text-center text-sm leading-tight transition-colors group-hover:underline"
                                                        >
                                                            {{ thumb.title }}
                                                        </div>
                                                        <div
                                                            v-if="
                                                                thumb.display_tag &&
                                                                thumb.tag_text
                                                            "
                                                            class="menu-thumbnail-title text-center"
                                                            :data-tag="
                                                                thumb.display_tag &&
                                                                thumb.tag_text
                                                                    ? thumb.tag_text
                                                                    : null
                                                            "
                                                            :data-tag-color="
                                                                thumb.display_tag &&
                                                                thumb.tag_color
                                                                    ? thumb.tag_color
                                                                    : null
                                                            "
                                                        >
                                                            &nbsp;
                                                        </div>
                                                    </NuxtLink>
                                                </div>
                                            </div>
                                        </Transition>
                                    </div>

                                    <!-- Links Column -->
                                    <div v-else-if="'links' in column">
                                        <button
                                            v-if="column.title"
                                            class="flex w-full items-center justify-between py-4 text-left text-lg font-semibold text-grind transition-colors"
                                            @click="
                                                toggleColumn(
                                                    activeMenuIndex!,
                                                    columnIndex,
                                                )
                                            "
                                        >
                                            <span>{{ column.title }}</span>
                                            <Icon
                                                :name="
                                                    isColumnExpanded(
                                                        activeMenuIndex!,
                                                        columnIndex,
                                                    )
                                                        ? 'chevron-up'
                                                        : 'chevron-down'
                                                "
                                                class="text-base"
                                            />
                                        </button>
                                        <Transition
                                            enter-active-class="transition-all duration-300 ease-in-out"
                                            leave-active-class="transition-all duration-300 ease-in-out"
                                            enter-from-class="max-h-0 opacity-0"
                                            enter-to-class="max-h-screen opacity-100"
                                            leave-from-class="max-h-screen opacity-100"
                                            leave-to-class="max-h-0 opacity-0"
                                        >
                                            <ul
                                                v-show="
                                                    !column.title ||
                                                    isColumnExpanded(
                                                        activeMenuIndex!,
                                                        columnIndex,
                                                    )
                                                "
                                                class="mb-6 mt-2 flex flex-col gap-y-6"
                                            >
                                                <li
                                                    v-for="(
                                                        link, linkIndex
                                                    ) in column.links"
                                                    :key="linkIndex"
                                                >
                                                    <NuxtLink
                                                        :to="
                                                            isActive
                                                                ? urlFromStoryblokLinkField(
                                                                      link,
                                                                  ) +
                                                                  (link.anchor
                                                                      ? '#' +
                                                                        link.anchor
                                                                      : '')
                                                                : undefined
                                                        "
                                                        class="py-3 text-grind transition-colors hover:text-brew hover:underline"
                                                        @click="open = false"
                                                    >
                                                        {{ link.label }}
                                                    </NuxtLink>
                                                </li>
                                            </ul>
                                        </Transition>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </FadeTransition>
        </nav>
    </CollapsibleRoot>
</template>

<script setup lang="ts">
import { CollapsibleRoot, CollapsibleTrigger } from 'reka-ui';
import type { SiteNavType } from '#shared/types/SiteNavType';
import FadeTransition from '~/components/transitions/FadeTransition.vue';
import LogoLink from '~/layouts/includes/menu/LogoLink.vue';
import { urlFromStoryblokLinkField } from '~/utils/helpers';

const props = withDefaults(
    defineProps<{
        data: SiteNavType | null;
        isActive?: boolean;
    }>(),
    {
        isActive: true,
    },
);

const route = useRoute();
const open = ref(false);
const activeMenuIndex = ref<number | null>(null);
const isSubmenuVisible = ref(false);
const expandedColumns = ref<Set<string>>(new Set());
const paddingBottom = ref(0);
const menuHeight = 64;
const topBarHeight = 51;
const additionalPaddingBottom = 30;

const activeSubmenu = computed(() => {
    if (activeMenuIndex.value !== null && props.data?.menu_dropdowns) {
        return props.data.menu_dropdowns[activeMenuIndex.value];
    }
    return null;
});

const getColumnKey = (menuIndex: number, columnIndex: number) => {
    return `${menuIndex}-${columnIndex}`;
};

const isColumnExpanded = (menuIndex: number, columnIndex: number) => {
    return expandedColumns.value.has(getColumnKey(menuIndex, columnIndex));
};

const toggleColumn = (menuIndex: number, columnIndex: number) => {
    const key = getColumnKey(menuIndex, columnIndex);
    if (expandedColumns.value.has(key)) {
        expandedColumns.value.delete(key);
    } else {
        expandedColumns.value.add(key);
    }
};

const openSubmenu = (index: number) => {
    activeMenuIndex.value = index;
    isSubmenuVisible.value = true;
    expandedColumns.value.clear(); // Reset expanded columns when opening a new submenu
};

const closeSubmenu = () => {
    isSubmenuVisible.value = false;
    // Don't clear activeMenuIndex immediately - let the transition finish first
    expandedColumns.value.clear(); // Reset expanded columns when closing submenu
};

const onSubmenuTransitionEnd = () => {
    // Only reset the menu index after the transition completes
    if (!isSubmenuVisible.value) {
        activeMenuIndex.value = null;
    }
};

watch(
    () => route.fullPath,
    () => {
        open.value = false;
    },
);

watch(
    () => open.value,
    (isOpen) => {
        if (isOpen) {
            document.documentElement.style.overflow = 'hidden';
            if (window.scrollY < topBarHeight) {
                paddingBottom.value = topBarHeight - window.scrollY;
            }
            paddingBottom.value += menuHeight + additionalPaddingBottom;
        } else {
            paddingBottom.value = 0;
            isSubmenuVisible.value = false;
            activeMenuIndex.value = null; // Reset submenu when closing
            expandedColumns.value.clear(); // Reset expanded columns when closing menu
            document.documentElement.style.overflow = '';
        }
    },
);
</script>
<style scoped>
.menu-thumbnail-title[data-tag]::before {
    content: attr(data-tag);
    display: inline-block;
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 700;
    color: white;
}

.menu-thumbnail-title[data-tag-color='brew']::before {
    background-color: theme('colors.brew');
}
.menu-thumbnail-title[data-tag-color='spice']::before {
    background-color: theme('colors.spice');
}
.menu-thumbnail-title[data-tag-color='vibe']::before {
    background-color: theme('colors.vibe.DEFAULT');
}
.menu-thumbnail-title[data-tag-color='drip']::before {
    background-color: theme('colors.drip');
}
.menu-thumbnail-title[data-tag-color='grind']::before {
    background-color: theme('colors.grind.DEFAULT');
}
</style>
